/**
 * Accessibility behaviour for the Events Manager calendar modals.
 *
 * The plugin's modal implementation (includes/js/src/parts/modal.js) opens a
 * div, moves it to <body> and does nothing else: focus is never moved into the
 * dialog, Escape does not close it, and the page behind stays reachable. A
 * keyboard user who activates a date link therefore ends up tabbing through the
 * calendar underneath an overlay they cannot see or dismiss.
 *
 * We cannot patch the plugin's JS, but it dispatches `em_modal_open` and
 * `em_modal_close` on the document, which is enough to layer the missing
 * behaviour on top. The dialog role, accessible name and a real <button> close
 * control come from the template overrides in
 * regents/plugins/events-manager/calendar/.
 */
(function () {
	"use strict";

	var FOCUSABLE = [
		"a[href]",
		"button:not([disabled])",
		"input:not([disabled]):not([type='hidden'])",
		"select:not([disabled])",
		"textarea:not([disabled])",
		"[tabindex]:not([tabindex='-1'])",
	].join(",");

	// The plugin's open transition runs for ~0.3s with a 0.15s delay on the close
	// button, so allow a little over half a second for the dialog to become
	// focusable before giving up.
	var FOCUS_RETRY_MS = 60;
	var FOCUS_ATTEMPTS = 10;

	// The element that had focus before the dialog opened, so we can put it back.
	var previouslyFocused = null;
	var activeModal = null;

	function getFocusable(modal) {
		return Array.prototype.filter.call(modal.querySelectorAll(FOCUSABLE), function (el) {
			// offsetParent is null for anything inside a display:none subtree.
			return el.offsetParent !== null || window.getComputedStyle(el).position === "fixed";
		});
	}

	function focusFirst(modal, attempt) {
		// Prefer the close button so the very first Tab does not leave the dialog,
		// and so the user is told immediately how to get out.
		var closeButton = modal.querySelector(".em-close-modal");
		var target = closeButton || getFocusable(modal)[0];

		if (target) {
			target.focus();
		} else {
			// Nothing focusable inside — make the dialog itself the focus target.
			modal.setAttribute("tabindex", "-1");
			modal.focus();
		}

		if (modal.contains(document.activeElement)) {
			return;
		}

		// The plugin fades the dialog in (and delays the close button by 0.15s),
		// so for the first frames it is still visibility:hidden and focus() is a
		// no-op. Retry across that transition rather than guessing at a delay.
		attempt = attempt || 0;
		if (attempt < FOCUS_ATTEMPTS) {
			window.setTimeout(function () {
				if (activeModal === modal) {
					focusFirst(modal, attempt + 1);
				}
			}, FOCUS_RETRY_MS);
		}
	}

	function trapFocus(event) {
		if (event.key !== "Tab" || !activeModal) {
			return;
		}

		var focusable = getFocusable(activeModal);
		if (focusable.length === 0) {
			event.preventDefault();
			return;
		}

		var first = focusable[0];
		var last = focusable[focusable.length - 1];

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		} else if (!activeModal.contains(document.activeElement)) {
			// Focus escaped the dialog some other way (e.g. browser UI round trip).
			event.preventDefault();
			first.focus();
		}
	}

	function onKeydown(event) {
		if (!activeModal) {
			return;
		}

		if (event.key === "Escape" || event.key === "Esc") {
			event.preventDefault();
			closeActiveModal();
			return;
		}

		trapFocus(event);
	}

	function closeActiveModal() {
		if (!activeModal) {
			return;
		}

		// Click the close control rather than calling the plugin's closeModal()
		// directly: that function is a script-scoped `let`, not a property of
		// window, so it is not reliably reachable from here. The click goes
		// through the plugin's own delegated handler, so its transitions and DOM
		// re-parenting still run exactly as normal.
		var closeButton = activeModal.querySelector(".em-close-modal");
		if (closeButton) {
			closeButton.click();
		}
	}

	/**
	 * Hide the rest of the page from assistive tech while the dialog is open.
	 * The plugin moves the modal to <body>, so every other top-level element
	 * can be safely marked.
	 */
	function setBackgroundHidden(modal, hidden) {
		Array.prototype.forEach.call(document.body.children, function (child) {
			if (child === modal || child.nodeName === "SCRIPT" || child.nodeName === "STYLE") {
				return;
			}

			if (hidden) {
				// Remember whether the element already had aria-hidden so we can
				// restore its original state rather than clobbering it.
				if (child.hasAttribute("aria-hidden")) {
					child.setAttribute("data-em-a11y-prev-hidden", child.getAttribute("aria-hidden"));
				}
				child.setAttribute("aria-hidden", "true");
			} else {
				if (child.hasAttribute("data-em-a11y-prev-hidden")) {
					child.setAttribute("aria-hidden", child.getAttribute("data-em-a11y-prev-hidden"));
					child.removeAttribute("data-em-a11y-prev-hidden");
				} else {
					child.removeAttribute("aria-hidden");
				}
			}
		});
	}

	document.addEventListener("em_modal_open", function (event) {
		var modal = event.detail && event.detail.modal;

		// The plugin hands us a jQuery object.
		if (modal && modal.jquery) {
			modal = modal[0];
		}
		if (!modal) {
			return;
		}

		previouslyFocused = document.activeElement;
		activeModal = modal;

		document.addEventListener("keydown", onKeydown, true);

		// Move focus before hiding the background, otherwise the element that
		// still holds focus is briefly inside an aria-hidden subtree. One frame
		// of delay lets the plugin's open transition settle first.
		window.requestAnimationFrame(function () {
			if (activeModal !== modal) {
				return;
			}
			focusFirst(modal);
			setBackgroundHidden(modal, true);
		});
	});

	document.addEventListener(
		"em_modal_close",
		function (event) {
			var modal = event.detail && event.detail.modal;
			if (modal && modal.jquery) {
				modal = modal[0];
			}
			if (!modal || modal !== activeModal) {
				return;
			}

			document.removeEventListener("keydown", onKeydown, true);
			setBackgroundHidden(modal, false);
			activeModal = null;

			// Return focus to whatever opened the dialog.
			if (previouslyFocused && typeof previouslyFocused.focus === "function") {
				previouslyFocused.focus();
			}
			previouslyFocused = null;
		},
		true
	);
})();
